import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { enhancedProductPhotoApi } from '../../redux/apis/endpoints/productPhotoApi';
import { BACKEND_URL } from '../../utils/config';
import ManageSubscriptionModal from './ManageSubscriptionModal';

const BRAND_TEAL = '#14b8a6';

/* ── Success modal shown after redirect return ── */
const SubscriptionSuccessModal = ({ creditsGranted, newBalance, onClose }) => {
  if (creditsGranted == null) return null;
  return ReactDOM.createPortal(
    <>
      <style>{`@keyframes subSuccessSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      >
        <div style={{ background: '#fff', borderRadius: 16, maxWidth: 400, width: '100%', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', animation: 'subSuccessSlideUp 0.25s ease', padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#059669' }}>
            <svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="3" /><path d="M14 24l7 7 13-13" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>You're subscribed to Creator!</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
            {creditsGranted > 0 ? `+${creditsGranted} credits added · ` : ''}Balance: {newBalance ?? '--'} credits · renews monthly
          </div>
          <button
            onClick={onClose}
            style={{ marginTop: 20, padding: '10px 32px', background: BRAND_TEAL, color: '#fff', border: 'none', borderRadius: 9999, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Continue
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

/**
 * App-root subscription handler (react-router v5). Works on ANY route:
 *   - ?subscription=success&session_id=… → confirm the subscription, show a
 *     success modal, and invalidate the Credits RTK tag so the balance refreshes.
 *   - ?manage=subscription → open the manage/cancel popup (e.g. from email).
 */
const SubscriptionManager = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isManageOpen, setManageOpen] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  // Confirm-on-return → instant credit refresh + success modal
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get('subscription') !== 'success' || !p.get('session_id')) return;
    const sessionId = p.get('session_id');
    (async () => {
      let confirmResult = null;
      try {
        const res = await fetch(`${BACKEND_URL}/credit-packages/confirm-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ sessionId })
        });
        confirmResult = await res.json();
      } catch (e) {
        /* webhook is the backstop */
      } finally {
        dispatch(enhancedProductPhotoApi.util.invalidateTags(['Credits']));
        const next = new URLSearchParams(location.search);
        next.delete('subscription');
        next.delete('session_id');
        history.replace({ pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : '' });

        // Show success modal
        setSuccessInfo({
          creditsGranted: 120,
          newBalance: confirmResult && confirmResult.creditBalance != null
            ? confirmResult.creditBalance : null
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Open manage popup from the email link
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get('manage') === 'subscription') setManageOpen(true);
  }, [location.search]);

  const close = useCallback(() => {
    setManageOpen(false);
    const p = new URLSearchParams(location.search);
    if (p.get('manage')) {
      p.delete('manage');
      history.replace({ pathname: location.pathname, search: p.toString() ? `?${p.toString()}` : '' });
    }
  }, [history, location.pathname, location.search]);

  return (
    <>
      <SubscriptionSuccessModal
        creditsGranted={successInfo ? successInfo.creditsGranted : null}
        newBalance={successInfo ? successInfo.newBalance : null}
        onClose={() => setSuccessInfo(null)}
      />
      <ManageSubscriptionModal
        isOpen={isManageOpen}
        onClose={close}
        onChanged={() => dispatch(enhancedProductPhotoApi.util.invalidateTags(['Credits']))}
      />
    </>
  );
};

export default SubscriptionManager;
